import { Component, DestroyRef, Input, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot } from '@angular/router';

import { DispatcherService } from '@frontend/sports/common/core/utils/dispatcher';
import { DeviceService, UserService } from '@frontend/vanilla/core';
import { distinctUntilChanged, skip } from 'rxjs';

import { DetailedEventViewModel, DispatcherEvent } from '../event-details-common/event-details-common.models';
import { EVENT_DETAILS_MODAL_CONTEXT, EventModalDetailsContext } from '../event-details-common/modal/event-details-modal-context';
import { EventDetailsModalService } from './event-details-modal.service';
import { EventDetailsResolver } from './event-details.resolver';

@Component({
    selector: 'ms-event-details-modal',
    templateUrl: './event-details-modal.component.html',
    host: {
        '[class.scrollable]': 'isScrollable()',
    },
    viewProviders: [
        {
            provide: EVENT_DETAILS_MODAL_CONTEXT,
            useValue: {
                isEnabled: true,
            } satisfies EventModalDetailsContext,
        },
    ],
    standalone: false,
})
export class EventDetailsModalComponent {
    @Input() detailedEventModel: DetailedEventViewModel;
    @Input() route: ActivatedRouteSnapshot;

    readonly isScrollable = signal(true);

    private destroyRef = inject(DestroyRef);
    private dispatcher = inject(DispatcherService);

    constructor(
        private eventDetailsModalService: EventDetailsModalService,
        private eventDetailsResolver: EventDetailsResolver,
        private deviceService: DeviceService,
        public userService: UserService,
    ) {
        this.deviceService.orientation.pipe(distinctUntilChanged(), skip(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => this.updateData());

        this.dispatcher
            .on<boolean>(DispatcherEvent.EventDetails.Expanded)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((isExpanded) => this.isScrollable.set(!isExpanded));
    }

    close(): void {
        this.eventDetailsModalService.close();
    }

    private async updateData(): Promise<void> {
        this.detailedEventModel = await this.eventDetailsResolver.modalResolve(this.route);
    }
}
